import React, { useCallback, useRef, useState, useEffect } from "react";
import LandingPage from "./components/landing_page";
import EditorHeader from "./components/editor_header";
import DaysPicker from "./components/days_picker";
import HourControls from "./components/hour_controls";
import AddClassModal from "./components/add_class_modal";
import EditClassModal from "./components/edit_class_modal";
import SettingsModal from "./components/settings_modal";
import BackgroundCropperModal from "./components/background_cropper_modal";
import TimetableGrid from "./components/timetable_grid";
import ConfirmModal from "./components/confirm_modal";
import useLocalStorage from "./hooks/use_local_storage";
import useTheme from "./hooks/use_theme";
import { exportTimetableImage } from "./utils/export_timetable";
import { checkOverlap, canDeleteHour } from "./utils/schedule";
import { DEFAULT_HOURS, DEFAULT_SETTINGS } from "./constants/defaults";
import { DEFAULT_SELECTED_DAYS } from "./constants/days";
import { DEVICE_OPTIONS, getPreviewScale, getSafeAreaPreview, getCardSize, getTextScale, isPhoneView, DEFAULT_DEVICE_ID } from "./constants/devices";

const STORAGE_KEYS = {
  DEVICE: "imey_scheduler:device",
  HOURS: "imey_scheduler:hours",
  DAYS: "imey_scheduler:days",
  CLASSES: "imey_scheduler:classes",
  SETTINGS: "imey_scheduler:settings",
  BACKGROUND: "imey_scheduler:background",
};

function createClassId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function App() {
  const [device, setDevice] = useLocalStorage(STORAGE_KEYS.DEVICE, null);
  const [visibleHours, setVisibleHours] = useLocalStorage(STORAGE_KEYS.HOURS, DEFAULT_HOURS);
  const [selectedDays, setSelectedDays] = useLocalStorage(
    STORAGE_KEYS.DAYS,
    DEFAULT_SELECTED_DAYS
  );
  const [classes, setClasses] = useLocalStorage(STORAGE_KEYS.CLASSES, []);
  const [settings, setSettings] = useLocalStorage(STORAGE_KEYS.SETTINGS, DEFAULT_SETTINGS, {
    mergeWithDefaults: true,
  });
  const [previewSettings, setPreviewSettings] = useState(settings);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showLanding, setShowLanding] = useState(false);

  const { theme, toggleTheme } = useTheme();

  const imageInputRef = useRef(null);
  const timetableRef = useRef(null);

  const backgroundSaveWarnedRef = useRef(false);
  const notifyBackgroundSaveFailed = useCallback(() => {
    if (backgroundSaveWarnedRef.current) return;
    backgroundSaveWarnedRef.current = true;
    alert(
      "Your background image is too large to persist in this browser. " +
        "It will not survive a page refresh."
    );
  }, []);

  const [backgroundImage, setBackgroundImage] = useLocalStorage(
    STORAGE_KEYS.BACKGROUND,
    null,
    { onError: notifyBackgroundSaveFailed }
  );
  const [rawImage, setRawImage] = useState(null);

  useEffect(() => {
    if (showSettings) setPreviewSettings(settings);
  }, [showSettings, settings]);

  useEffect(() => {
    const savedDevice = device
      ? DEVICE_OPTIONS.find((entry) => entry.id === device.id) ?? null
      : null;
    if (savedDevice && isPhoneView() && savedDevice.category !== "Phones") {
      const fallback = DEVICE_OPTIONS.find((entry) => entry.id === DEFAULT_DEVICE_ID);
      if (fallback) setDevice(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeSettings = showSettings ? previewSettings : settings;
  const currentDevice = device
    ? DEVICE_OPTIONS.find((entry) => entry.id === device.id) ?? null
    : null;
  const previewSize = currentDevice ? getPreviewScale(currentDevice) : null;
  const safeAreaOffset = currentDevice
    ? getSafeAreaPreview(currentDevice, previewSize.width)
    : 0;
  const cardSize = currentDevice ? getCardSize(currentDevice, previewSize) : null;
  const textScale = cardSize ? getTextScale(cardSize.width) : 1;
  const sortedHours = [...visibleHours].sort((a, b) => a - b);

  function addHour(hour) {
    setVisibleHours((previous) => [...previous, hour].sort((a, b) => a - b));
  }

  function removeHour(hour) {
    setVisibleHours((previous) => previous.filter((h) => h !== hour));
  }

  function canRemoveHour(hour) {
    return canDeleteHour(classes, selectedDays, hour);
  }

  function handleAddClass(newClass) {
    const withId = { ...newClass, id: createClassId() };
    if (checkOverlap(classes, withId)) {
      alert("Overlapping class detected. Please adjust the time or days.");
      return;
    }
    setClasses((previous) => [...previous, withId]);
    setShowAddModal(false);
  }

  function handleEditClass(updated) {
    if (checkOverlap(classes, updated, updated.id)) {
      alert("Overlapping class detected. Please adjust the time or days.");
      return false;
    }
    setClasses((previous) =>
      previous.map((entry) => (entry.id === updated.id ? updated : entry))
    );
    return true;
  }

  function handleDeleteClass(id) {
    setClasses((previous) => previous.filter((entry) => entry.id !== id));
  }

  function resetProgress() {
    setVisibleHours(DEFAULT_HOURS);
    setSelectedDays(DEFAULT_SELECTED_DAYS);
    setClasses([]);
    setSettings(DEFAULT_SETTINGS);
    setBackgroundImage(null);
    setPreviewSettings(DEFAULT_SETTINGS);
    setShowResetConfirm(false);
  }

  function openClassEditor(entry) {
    setEditingClass(entry);
    setShowEditModal(true);
  }

  function onImageSelect(event) {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  }

  async function downloadTimetable() {
    if (!timetableRef.current || !currentDevice || !previewSize) return;
    try {
      await exportTimetableImage(timetableRef.current, currentDevice, previewSize.width);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed: " + (error.message || error));
    }
  }

  if (!currentDevice || showLanding) {
    return (
      <LandingPage
        currentDevice={currentDevice}
        theme={theme}
        onToggleTheme={toggleTheme}
        onCreate={(selected) => {
          if (!selected) return;
          setDevice(selected);
          setShowLanding(false);
        }}
      />
    );
  }

  return (
    <div className="app-editor">
      <EditorHeader
        device={currentDevice}
        theme={theme}
        onToggleTheme={toggleTheme}
        onDeviceSelect={setDevice}
        onBrandClick={() => setShowLanding(true)}
      />

      <div className="btn-strip">
        <div className="editor-row">
          <div className="btn-row">
            <button className="btn" onClick={() => setShowSettings(true)}>
              Settings
            </button>
            <button className="btn" onClick={() => imageInputRef.current?.click()}>
              Set Background Image
            </button>
            <button className="btn" onClick={() => setShowAddModal(true)}>
              + Add Class
            </button>
            <button className="btn btn--primary" onClick={downloadTimetable}>
              Download as Image
            </button>
            <button className="btn btn--danger" onClick={() => setShowResetConfirm(true)}>
              Reset
            </button>
          </div>
        </div>
      </div>

      <input
        type="file"
        accept="image/*"
        onChange={onImageSelect}
        ref={imageInputRef}
        className="hidden-input"
      />

      <div className="btn-strip">
        <div className="editor-row">
          <DaysPicker
            selectedDays={selectedDays}
            onSelectedDaysChange={setSelectedDays}
          />
          <HourControls
            visibleHours={visibleHours}
            canDeleteHour={canRemoveHour}
            onRemoveHour={removeHour}
            onAddHour={addHour}
          />
        </div>
      </div>

      <TimetableGrid
        gridRef={timetableRef}
        width={previewSize.width}
        height={previewSize.height}
        safeAreaOffset={safeAreaOffset}
        cardWidth={cardSize.width}
        cardHeight={cardSize.height}
        textScale={textScale}
        sortedHours={sortedHours}
        selectedDays={selectedDays}
        classes={classes}
        backgroundImage={backgroundImage}
        settings={activeSettings}
        onClassSelect={openClassEditor}
      />

      <EditClassModal
        visible={showEditModal}
        initialData={editingClass}
        days={selectedDays}
        onClose={() => setShowEditModal(false)}
        onEditClass={handleEditClass}
        onDeleteClass={(id) => {
          handleDeleteClass(id);
          setShowEditModal(false);
        }}
      />

      <AddClassModal
        visible={showAddModal}
        days={selectedDays}
        onAdd={handleAddClass}
        onClose={() => setShowAddModal(false)}
      />

      <SettingsModal
        visible={showSettings}
        settings={previewSettings}
        onChange={setPreviewSettings}
        onSave={() => {
          setSettings(previewSettings);
          setShowSettings(false);
        }}
        onClose={() => setShowSettings(false)}
      />

      {showCropper && rawImage && (
        <BackgroundCropperModal
          imageSrc={rawImage}
          width={currentDevice.width}
          height={currentDevice.height}
          onCropComplete={(croppedUrl) => {
            setBackgroundImage(croppedUrl);
            setShowCropper(false);
          }}
          onClose={() => setShowCropper(false)}
        />
      )}

      <ConfirmModal
        visible={showResetConfirm}
        title="Reset everything?"
        message="This will permanently delete your classes, settings, time rows, and background image. This cannot be undone."
        confirmLabel="Reset"
        onConfirm={resetProgress}
        onCancel={() => setShowResetConfirm(false)}
      />
    </div>
  );
}
